import React from 'react'

import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, type AddressCell, Alert } from '@/components/ui'
import { useApi, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

interface LocationHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

interface LocationHistory {
  sampleTime: Date
  latitude?: number
  longitude?: number
  location?: string
}

const LocationHistory: InsightDefinition<LocationHistoryParams, LocationHistory[]> = {
  active: true,
  slug: 'asset/location-history',
  category: 'asset',
  name: 'Location History',
  description: 'Display location history for an asset for a given date range',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom,
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: LocationHistoryParams) => {
      if (!configuration?.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }
        const fromdate = new Date(configuration.from)
        const todate = new Date(configuration.to)
        if (fromdate > todate) {
          return 'Start date must be before end date'
        }
        const dateValidation30Days = validateDateRangeWithin30Days(fromdate.toISOString(), todate.toISOString())
        if (dateValidation30Days !== true) {
          return dateValidation30Days
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: LocationHistoryParams) => {
    const path = `/insights/asset/location-history?${buildQuery(parameters)}`
    const response = await api.get<LocationHistory[]>({
      path,
      origin: 'LocationHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: LocationHistoryParams, title: string) => {
    const path = '/insights/asset/location-history'
    await downloadCsv(api, path, parameters, title)
  },

  toExcel: (data) => {
    const format = 'dd-MMM-yyyy hh:mm a'
    // Newest first, as the table shows them.
    const rows = [...data]
      .sort((a, b) => new Date(b.sampleTime).getTime() - new Date(a.sampleTime).getTime())
      .map((sample) => ({
        'Date/Time': DateTime.fromJSDate(new Date(sample.sampleTime)).toFormat(format),
        Location: sample.location ?? '',
      }))
    return { headers: ['Date/Time', 'Location'], rows }
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={2} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const parameters = configuration.parameters as LocationHistoryParams
    const format = 'dd-MMM-yyyy hh:mm a'
    const headers = ['Date/Time', 'Location']
    const body = data
      .sort((a, b) => new Date(b.sampleTime).getTime() - new Date(a.sampleTime).getTime())
      .map(
        (sample): Record<string, string | AddressCell> => ({
          'Date/Time': DateTime.fromJSDate(new Date(sample.sampleTime)).toFormat(format),
          Location: genericLocationDrilldown({
            configuration,
            address: sample.location ?? undefined,
            latitude: sample.latitude,
            longitude: sample.longitude,
            companyId: parameters.companyId,
            markerFields: {
              Time: new Date(sample.sampleTime).toLocaleString(),
            },
          }),
        })
      )

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ 'Date/Time': 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as LocationHistoryParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'LocationHistory#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default LocationHistory
